/**
 * Release sections API client (dashboard rows, nav badges, calendar nudge, Brand Portfolio inbox).
 * Contract: DASHBOARD-NAV-PLAN.md section 1.6, mirrored by types/releaseSections.ts.
 */

import apiClient from './api';
import type {
  BrandReleaseSections,
  NewlyReleased,
  ReleaseBadges,
  ReleaseInboxCounts,
  ReleaseInboxPage,
  ReleaseInboxPageParams,
  ReleaseSurface,
} from '@/types/releaseSections';

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const releaseSectionsService = {
  /** Every section that applies to the viewer in one brand (dashboard rows) */
  getBrandSections: async (teamId: number, limit: number): Promise<BrandReleaseSections> => {
    const response = await apiClient.get<Envelope<BrandReleaseSections>>(
      `/api/teams/${teamId}/release-sections`,
      { params: { limit } }
    );
    return response.data.data;
  },

  /** Counts only (nav badges); cheap, no media */
  getBadges: async (teamId: number): Promise<ReleaseBadges> => {
    const response = await apiClient.get<Envelope<ReleaseBadges>>(
      `/api/teams/${teamId}/release-badges`
    );
    return response.data.data;
  },

  /** Operators: items released since `since` (default: last calendar visit, max 7 days back) */
  getNewlyReleased: async (teamId: number, since?: string | null): Promise<NewlyReleased> => {
    const response = await apiClient.get<Envelope<NewlyReleased>>(
      `/api/teams/${teamId}/release-sections/newly-released`,
      { params: since ? { since } : {} }
    );
    return response.data.data;
  },

  /**
   * Stamp a surface as seen. `asOf` is the server cut-off the surface was loaded with
   * (never the client clock). Moves forward only.
   */
  markSeen: async (teamId: number, surface: ReleaseSurface, asOf?: string): Promise<void> => {
    await apiClient.put(`/api/teams/${teamId}/surfaces/${surface}/seen`, asOf ? { asOf } : {});
  },

  /** One page of a section across the portfolio (staff only) */
  getInboxPage: async (params: ReleaseInboxPageParams): Promise<ReleaseInboxPage> => {
    const response = await apiClient.get<Envelope<ReleaseInboxPage>>('/api/release/inbox', {
      params: {
        section: params.section,
        ...(params.teamId ? { teamId: params.teamId } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
        limit: params.limit ?? 20,
      },
    });
    return response.data.data;
  },

  getInboxCounts: async (): Promise<ReleaseInboxCounts> => {
    const response = await apiClient.get<Envelope<ReleaseInboxCounts>>('/api/release/inbox/counts');
    return response.data.data;
  },
};
